/**
 * Constants and utilities for age groups in the story generator
 */

// Age group IDs and display names
export const AGE_GROUPS = [
  { id: 'babies', name: 'Babies', ageRange: '0-1 years' },
  { id: 'toddlers', name: 'Toddlers', ageRange: '1-2 years' },
  { id: 'preschoolers-young', name: 'Young Preschoolers', ageRange: '2-4 years' },
  { id: 'preschoolers-older', name: 'Older Preschoolers', ageRange: '4-6 years' },
  { id: 'schoolchildren', name: 'School Children', ageRange: '6-12 years' },
  { id: 'teenagers', name: 'Teenagers', ageRange: '12-18 years' }
];

/**
 * Gets the detailed settings for a specific age group
 * @param {string} ageGroupId - The ID of the age group
 * @returns {Object} - The age group settings
 */
export function getAgeGroupSettings(ageGroupId) {
  // Default to preschoolers-older if age group not found
  let settings;
  
  switch (ageGroupId) {
    case 'babies':
      settings = {
        vocabularyLevel: 'very simple words, lots of repetition',
        sentenceComplexity: 'single words or very simple 2-3 word phrases',
        storyLength: 'very short (2-3 scenes)',
        appropriateThemes: 'colors, simple objects, familiar routines, gentle sounds',
        specialInstructions: 'filled with rhythm, rhyme and repetition. Use onomatopoeia (sound words) and focus on sensory elements.'
      };
      break;
      
    case 'toddlers':
      settings = {
        vocabularyLevel: 'simple concrete words, familiar objects and actions',
        sentenceComplexity: 'simple 3-4 word sentences',
        storyLength: 'short (3-4 scenes)',
        appropriateThemes: 'daily routines, familiar animals, simple emotions, family',
        specialInstructions: 'interactive with simple questions. Include repetitive phrases and predictable sequences.'
      };
      break;
      
    case 'preschoolers-young':
      settings = {
        vocabularyLevel: 'expanded vocabulary, familiar concepts plus a few new words',
        sentenceComplexity: 'simple sentences with some compound sentences',
        storyLength: 'moderate (4-5 scenes)',
        appropriateThemes: 'friendship, sharing, simple problem-solving, imagination',
        specialInstructions: 'engaging with simple narrative structure. Include basic emotional lessons and gentle humor.'
      };
      break;
      
    case 'preschoolers-older':
      settings = {
        vocabularyLevel: 'varied vocabulary with some new words in context',
        sentenceComplexity: 'mix of simple and compound sentences',
        storyLength: 'moderate (4-6 scenes)',
        appropriateThemes: 'cooperation, courage, basic morals, adventure',
        specialInstructions: 'engaging with clear beginning, middle and end. Include simple lessons and occasional humor.'
      };
      break;
      
    case 'schoolchildren':
      settings = {
        vocabularyLevel: 'rich vocabulary with context for new words',
        sentenceComplexity: 'varied sentence structure including complex sentences',
        storyLength: 'longer (5-6 scenes)',
        appropriateThemes: 'friendship challenges, bravery, perseverance, mystery, mild fantasy',
        specialInstructions: 'more sophisticated plot with minor challenges and clear resolution. Include gentle suspense and moral lessons.'
      };
      break;
      
    case 'teenagers':
      settings = {
        vocabularyLevel: 'advanced vocabulary appropriate for young adults',
        sentenceComplexity: 'varied and complex sentence structures',
        storyLength: 'full (6 scenes)',
        appropriateThemes: 'identity, relationships, meaningful challenges, growth, fantasy',
        specialInstructions: 'deeper character development and more complex plot. Include nuanced morals and thoughtful resolution.'
      };
      break;
      
    default:
      // Default to preschoolers-older if not found
      settings = getAgeGroupSettings('preschoolers-older');
  }
  
  // Combine with the basic info
  const ageGroup = AGE_GROUPS.find(group => group.id === ageGroupId) || 
    AGE_GROUPS.find(group => group.id === 'preschoolers-older');
  
  return {
    ...ageGroup,
    ...settings
  };
}

/**
 * Returns the age group that matches the provided age in years
 * @param {number} age - The age in years
 * @returns {Object} - The matching age group
 */
export function getAgeGroupByAge(age) {
  if (age < 1) return getAgeGroupSettings('babies');
  if (age < 2) return getAgeGroupSettings('toddlers');
  if (age < 4) return getAgeGroupSettings('preschoolers-young');
  if (age < 6) return getAgeGroupSettings('preschoolers-older');
  if (age < 12) return getAgeGroupSettings('schoolchildren');
  return getAgeGroupSettings('teenagers');
}

/**
 * Gets an array of just the basic age group objects for selection UI
 * @returns {Array} - Array of age group objects with id, name and ageRange
 */
export function getAgeGroupsForSelector() {
  return AGE_GROUPS;
}
